// components/UI.tsx

import React, {
  ReactNode,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  AnchorHTMLAttributes,
  TextareaHTMLAttributes,
  SelectHTMLAttributes,
  HTMLAttributes
} from 'react';
import clsx from 'clsx';

type WithChildren<T> = T & { children?: ReactNode };

// ----------- Basic HTML Components -----------

type ButtonProps = WithChildren<ButtonHTMLAttributes<HTMLButtonElement>>;
const Button = ({ children, className, ...props }: ButtonProps) => (
  <button
    className={clsx('default_button', className)}
    {...props}
  >
    {children}
  </button>
);

type InputProps = InputHTMLAttributes<HTMLInputElement>;
const Input = ({ className, ...props }: InputProps) => (
  <input
    className={clsx('default_input', className)}
    {...props}
  />
);

type LinkProps = WithChildren<AnchorHTMLAttributes<HTMLAnchorElement>>;
const Link = ({ children, className, ...props }: LinkProps) => (
  <a
    className={clsx('default_link', className)}
    {...props}
  >
    {children}
  </a>
);

type TextAreaProps = WithChildren<TextareaHTMLAttributes<HTMLTextAreaElement>>;
const TextArea = ({ children, className, ...props }: TextAreaProps) => (
  <textarea
    className={clsx('default_textarea', className)}
    {...props}
  >
    {children}
  </textarea>
);

type SelectProps = WithChildren<SelectHTMLAttributes<HTMLSelectElement>>;
const Select = ({ children, className, ...props }: SelectProps) => (
  <select
    className={clsx('default_select', className)}
    {...props}
  >
    {children}
  </select>
);

// ----------- View/Text Components -----------

type ViewProps = WithChildren<HTMLAttributes<HTMLDivElement>>;
const View = ({ children, className, ...props }: ViewProps) => (
  <div className={clsx('default_view', className)} {...props}>
    {children}
  </div>
);

type TextProps = WithChildren<HTMLAttributes<HTMLSpanElement>>;
const Text = ({ children, className, ...props }: TextProps) => (
  <p className={clsx('default_text', className)} {...props}>
    {children}
  </p>
);

const Label = ({ children, className, ...props }: TextProps) => (
  <label className={clsx('default_label', className)} {...props}>
    {children}
  </label>
);

const Span = ({ children, className, ...props }: TextProps) => (
  <span className={clsx('default_span', className)} {...props}>
    {children}
  </span>
);

// ----------- Headings -----------

type HeadingProps = WithChildren<HTMLAttributes<HTMLHeadingElement>>;

const H1 = ({ children, className, ...props }: HeadingProps) => (
  <h1 className={clsx('default_h1', className)} {...props}>
    {children}
  </h1>
);
const H2 = ({ children, className, ...props }: HeadingProps) => (
  <h2 className={clsx('default_h2', className)} {...props}>
    {children}
  </h2>
);
const H3 = ({ children, className, ...props }: HeadingProps) => (
  <h3 className={clsx('default_h3', className)} {...props}>
    {children}
  </h3>
);
const H4 = ({ children, className, ...props }: HeadingProps) => (
  <h4 className={clsx('default_h4', className)} {...props}>
    {children}
  </h4>
);
const H5 = ({ children, className, ...props }: HeadingProps) => (
  <h5 className={clsx('default_h5', className)} {...props}>
    {children}
  </h5>
);
const H6 = ({ children, className, ...props }: HeadingProps) => (
  <h6 className={clsx('default_h6', className)} {...props}>
    {children}
  </h6>
);

// ----------- Exports -----------

export {
  Button,
  Input,
  Link,
  TextArea,
  Select,
  View,
  Text,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  Label,
  Span
};
